/**
 * Workflow Upload Page
 *
 * Simplified workflow upload: Upload a single ComfyUI API JSON file + metadata.
 * The backend will auto-detect parameters and generate the workflow package.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import { useTenant } from '../../contexts/TenantContext';


interface WorkflowUploadProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

export const WorkflowUpload: React.FC<WorkflowUploadProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const navigate = useNavigate();
  const { currentTenant, currentUser } = useTenant();

  const [comfyWorkflowFile, setComfyWorkflowFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Auto-populate display name from filename when file is selected
  const handleFileChange = (file: File | null) => {
    setComfyWorkflowFile(file);
    if (file && !displayName) {
      // Convert filename to display name: "furniture_MaterialSwap_API.json" → "Furniture MaterialSwap API"
      const nameWithoutExt = file.name.replace(/\.json$/i, '');
      const formatted = nameWithoutExt
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      setDisplayName(formatted);
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];
    if (!comfyWorkflowFile) errors.push('ComfyUI workflow file is required');
    if (comfyWorkflowFile && !comfyWorkflowFile.name.endsWith('.json')) errors.push('Workflow file must be a JSON file');
    if (!displayName) errors.push('Workflow name is required');
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleUpload = async () => {
    setError(null);
    setValidationErrors([]);
    if (!validateForm()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('comfyWorkflow', comfyWorkflowFile!);
      formData.append('displayName', displayName);

      // Only include optional fields if they have values
      if (description.trim()) formData.append('description', description);
      if (tags.trim()) {
        const tagsArray = tags.split(',').map(t => t.trim()).filter(t => t);
        if (tagsArray.length > 0) {
          formData.append('tags', JSON.stringify(tagsArray));
        }
      }

      const response = await fetch(`${apiBaseUrl}/api/tenants/${tenantId}/workflows`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Upload failed: ${response.statusText}`);
      }
      const result = await response.json();
      const isUpdate = result.isUpdate || false;
      const message = isUpdate
        ? `Workflow updated successfully to version ${result.metadata.version}!`
        : 'Workflow uploaded successfully! Parameters were auto-detected from your ComfyUI workflow.';
      alert(message);
      navigate('/workflows');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => { navigate('/workflows'); };

  const inputCls   = 'w-full px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast placeholder:text-gray-400';
  const textAreaCls = `${inputCls} resize-y`;
  const lbl         = 'block text-label font-medium text-gray-700 mb-1';
  const hint        = 'mt-1 text-meta text-gray-400';
  const sectionCard = 'bg-white rounded-lg border border-gray-200 shadow-xs p-6 flex flex-col gap-4';

  return (
    <div className="min-h-screen flex flex-col bg-surface-app">
      <TopBar tenantName={currentTenant?.name || 'Workspace'} userEmail={currentUser?.email || ''} />

      <div className="px-8 py-6 max-w-2xl mx-auto w-full flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Upload Workflow</h1>
          <button type="button" onClick={handleCancel}
            className="px-4 py-2.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast">
            Cancel
          </button>
        </div>

        {/* File section */}
        <div className={sectionCard}>
          <h2 className="text-body font-semibold text-gray-900">ComfyUI Workflow File</h2>
          <p className="text-label text-gray-500">Upload your ComfyUI workflow JSON file (exported as API format). The system will automatically detect parameters from CCStudio manifest nodes.</p>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="workflow-file-input" className={lbl}>Workflow JSON File *</label>
            <input id="workflow-file-input" type="file" accept=".json"
              onChange={e => handleFileChange(e.target.files?.[0] || null)}
              className="text-label text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-label file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" />
            {comfyWorkflowFile && <span className="text-meta text-success-dark">✓ {comfyWorkflowFile.name}</span>}
          </div>
        </div>

        {/* Details section */}
        <div className={sectionCard}>
          <h2 className="text-body font-semibold text-gray-900">Workflow Details</h2>
          <p className="text-label text-gray-500">The workflow name is auto-filled from the filename. You can edit it if needed.</p>
          <div>
            <label className={lbl}>Workflow Name *</label>
            <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
              placeholder="e.g., Furniture Material Swap" className={inputCls} />
            <p className={hint}>This name will be shown in the workflow selector</p>
          </div>
          <div>
            <label className={lbl}>Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Describe what this workflow does…" rows={3} className={textAreaCls} />
          </div>
          <div>
            <label className={lbl}>Tags (optional)</label>
            <input type="text" value={tags} onChange={e => setTags(e.target.value)}
              placeholder="e.g., furniture, materials, product (comma-separated)" className={inputCls} />
            <p className={hint}>Comma-separated tags for easier searching</p>
          </div>
        </div>

        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <div className="px-4 py-3 bg-warning-light border border-warning/30 rounded-lg">
            <div className="text-label font-semibold text-warning-dark mb-2">Please fix the following errors:</div>
            <ul className="list-disc list-inside flex flex-col gap-1">
              {validationErrors.map((err, i) => <li key={i} className="text-label text-warning-dark">{err}</li>)}
            </ul>
          </div>
        )}

        {error && (
          <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-lg text-label">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button type="button" onClick={handleUpload} disabled={uploading}
            className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 text-white text-body font-semibold rounded-md transition-colors duration-fast shadow-sm disabled:opacity-50">
            {uploading ? 'Uploading…' : 'Upload Workflow'}
          </button>
          <button type="button" onClick={handleCancel} disabled={uploading}
            className="px-5 py-3 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50">
            Cancel
          </button>
        </div>

        {/* Help */}
        <div className="bg-info-light border border-info/30 rounded-lg p-5">
          <h3 className="text-label font-semibold text-info-dark mb-3">ℹ️ How to prepare your workflow:</h3>
          <ol className="list-decimal list-inside flex flex-col gap-1.5">
            {['Open your workflow in ComfyUI',
              'Rename nodes you want as parameters to start with "CCStudio_Input..." etc.',
              'Export workflow as API format (right-click → "Save (API Format)")',
              'Upload the JSON file here',
            ].map((s, i) => <li key={i} className="text-label text-info-dark">{s}</li>)}
          </ol>
          <p className="mt-3 text-label text-info-dark">
            <strong>Note:</strong> If you upload a workflow with the same name as an existing one, it will update the existing workflow and increment the version automatically.
          </p>
        </div>
      </div>
    </div>
  );
};